import { buildElementId } from '../utils';

import {
  ValidateDatePicker,
  ValidateNumeric,
  ValidateRegExp,
  ValidateSegmented,
  ValidateSelector,
} from './ElementValidators';

const getValue = (answer) => {
  return (answer || { value: '' }).value;
};

const validateDate = (answer) => {
  return (answer || { isInvalid: true }).isInvalid;
};

const buildMessage = (caption, questions, getMessage, currentQuestion, labelKey, errorKey) => {
  if (!getMessage) return '';

  return `${caption} : ${
    questions[currentQuestion]?.question || getMessage(labelKey)
  } - ${getMessage(errorKey)}`;
};

const validateBirthDate = (
  animalType,
  newErrors,
  id,
  questions,
  validateAll,
  questionID,
  caption,
  getMessage
) => {
  const validateDatePicker = (dateOfBirth, elementId, errorText) => {
    return ValidateDatePicker(
      validateDate(dateOfBirth),
      questions,
      buildElementId(id, elementId),
      newErrors,
      validateAll,
      questionID,
      getMessage
        ? `${caption} : ${
            dateOfBirth?.question ||
            getMessage('labelText.time-of-birth')
          } - ${getMessage(errorText)}`
        : ''
    );
  };

  const validateAnimalType = (animalType) => {
    switch (animalType) {
      case 'Pedigree dog':
      case 'Mixed-breed dog':
        return validateDatePicker(
          questions.dateOfBirth,
          'ANIMAL_DATEOFBIRTH_DOG',
          'errorText.animalDateOfBirthDog'
        );
      case 'Horse':
        return validateDatePicker(
          questions.dateOfBirth,
          'ANIMAL_DATEOFBIRTH_HORSE',
          'errorText.animalDateOfBirthHorse'
        );
      case 'Pedigree cat':
      case 'Native breed cat':
        return validateDatePicker(
          questions.dateOfBirth,
          'ANIMAL_DATEOFBIRTH_CAT',
          'errorText.animalDateOfBirthCat'
        );
      default:
        return;
    }
  };

  return validateAnimalType(animalType.value);
};

const validateAnimalQuestions = (
  id,
  questions,
  validateAll,
  questionID,
  errors,
  caption,
  getMessage
) => {
  let newErrors = { add: [], remove: [] };
  const animalType = questions.animalType || { value: '' };

  newErrors = validateAnimalType(
    animalType,
    id,
    newErrors,
    validateAll,
    questionID,
    getMessage,
    caption
  );

  if (validateAll === true && animalType.value === 'choose') {
    newErrors.remove = removeErrors(id, errors, newErrors);
  } else if (animalType.value !== 'choose' && animalType.value !== '') {
    newErrors = validateAnimalDetails(
      animalType,
      id,
      questions,
      newErrors,
      validateAll,
      questionID,
      caption,
      getMessage
    );
  }

  return {
    add: newErrors.add.filter(Boolean),
    remove: newErrors.remove.filter(Boolean),
  };
};

const validateAnimalType = (
  animalType,
  id,
  newErrors,
  validateAll,
  questionID,
  getMessage,
  caption
) => {
  return ValidateSelector(
    animalType.value,
    buildElementId(id, 'ANIMAL_TYPE'),
    newErrors,
    validateAll,
    questionID,
    getMessage
      ? `${caption} : ${
          animalType.question || getMessage('labelText.insured-animal')
        } - ${getMessage('error.fill.in.missing.information')}`
      : ''
  );
};

const removeErrors = (id, errors, newErrors) => {
  return [
    ...newErrors.remove,
    ...errors
      .filter(
        (record) =>
          record.id.indexOf(id) !== -1 && record.id !== id + '_ANIMAL_TYPE'
      )
      .map((record) => record.id),
  ];
};

const validateAnimalDetails = (
  animalType,
  id,
  questions,
  newErrors,
  validateAll,
  questionID,
  caption,
  getMessage
) => {
  let nameMessage = getNameMessage(questions, caption, getMessage);

  if (['Horse'].includes(animalType.value)) {
    newErrors = ValidateSelector(
      getValue(questions.purposeOfHorse),
      buildElementId(id, 'ANIMAL_PURPOSE_OF_HORSE'),
      newErrors,
      validateAll,
      questionID,
      buildMessage(caption, questions, getMessage, "purposeOfHorse", "labelText.animalPurposeOfHorse", "error.fill.in.missing.information"),
    );
    newErrors = ValidateRegExp({
      value: getValue(questions.animalUELN),
      id: buildElementId(id, 'ANIMAL_UELN'),
      newErrors,
      validateAll,
      length: 1,
      questionID,
      regExp: '^[\\sa-zA-Z0-9åäöÅÄÖ.\\u0023,?!()+-/&:"\'@%€]+$',
      errorLabel: buildMessage(caption, questions, getMessage, "animalUELN", "labelText.animalUELN", "error.fill.in.missing.information"),
    });
  }

  newErrors = ValidateRegExp({
    value: getValue(questions.animalName),
    id: buildElementId(id, 'ANIMAL_NAME'),
    newErrors,
    validateAll,
    length: 1,
    questionID,
    regExp: '^[\\sa-zA-Z0-9åäöÅÄÖ.\\u0023,?!()+-/&:"\'@%€]+$',
    errorLabel: nameMessage,
  });

  if (['Pedigree dog', 'Pedigree cat', 'Horse'].includes(animalType.value)) {
    newErrors = ValidateSelector(
      getValue(questions.breed),
      buildElementId(id, 'ANIMAL_BREED'),
      newErrors,
      validateAll,
      questionID,
      getMessage
        ? `${caption} : ${
            questions.breed?.question || getMessage('labelText.breed')
          } - ${getMessage('error.fill.in.missing.information')}`
        : ''
    );
  }

  newErrors = validateBirthDate(
    animalType,
    newErrors,
    id,
    questions,
    validateAll,
    questionID,
    caption,
    getMessage
  );

  newErrors = ValidateSegmented(
    getValue(questions.gender),
    buildElementId(id, 'ANIMAL_GENDER'),
    newErrors,
    validateAll,
    questionID,
    getMessage
      ? `${caption} : ${
          questions.gender?.question || getMessage('labelText.AnimalGender')
        } - ${getMessage('error.fill.in.missing.information')}`
      : ''
  );

  const monetaryValues = {
    'Pedigree dog': 'labelText.monetary-value-dog',
    'Mixed-breed dog': 'labelText.monetary-value-dog',
    'Pedigree cat': 'labelText.monetary-value-pedigree-cat',
    'Native breed cat': 'labelText.monetary-value-pedigree-cat',
    Horse: 'labelText.monetary-value-horse',
  };

  let monetaryValueMessage = monetaryValues[animalType.value];

  newErrors = ValidateNumeric(
    getValue(questions.value),
    buildElementId(id, 'ANIMAL_VALUE'),
    newErrors,
    validateAll,
    1,
    99999,
    questionID,
    '^[1-9]\\d*(?:\\.\\d{1,2})?$',
    getMessage
      ? `${caption} : ${
          questions.value?.question || getMessage(monetaryValueMessage)
        } - ${getMessage('error.fill.in.missing.information')}`
      : ''
  );

  return newErrors;
};

const getNameMessage = (questions, caption, getMessage) => {
  let nameMessage =
    questions.animalName?.errorLabel ||
    (getMessage
      ? `${caption} : ${
          questions.animalName?.question || getMessage('labelText.AnimalName')
        } - ${getMessage('error.fill.in.missing.information')}`
      : '');
  return nameMessage.replace(
    '- undefined',
    (getMessage ?? questions.animalType.getMessage)(
      'error.fill.in.missing.information'
    )
  );
};

export default validateAnimalQuestions;